import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Search, Plus, Printer, Download, Eye } from 'lucide-react';
import { ReceiptModal } from './ReceiptModal';
import { Payment } from '../../types';
export const Payments: React.FC = () => {
  const {
    payments,
    invoices,
    patients,
    walkInClients,
    addPayment,
    updateInvoice,
    currentUser,
    logBillingAudit
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingPayment, setViewingPayment] = useState<Payment | null>(null);
  const [formData, setFormData] = useState({
    invoiceId: '',
    amount: '',
    method: 'Cash' as 'Cash' | 'Mobile Money' | 'Debit Card' | 'Credit Card' | 'Bank Transfer' | 'Insurance' | 'Corporate Account',
    notes: ''
  });
  const nextReceiptNumber = useMemo(() => {
    const prefix = 'REC-';
    const maxNum = payments.reduce((max, pay) => {
      if (pay.receiptNumber.startsWith(prefix)) {
        const num = parseInt(pay.receiptNumber.slice(prefix.length), 10);
        return !isNaN(num) && num > max ? num : max;
      }
      return max;
    }, 0);
    return `${prefix}${(maxNum + 1).toString().padStart(5, '0')}`;
  }, [payments]);
  const unpaidInvoices = invoices.filter(i => i.status === 'Unpaid' || i.status === 'Partially Paid' || i.status === 'Overdue');
  const filteredPayments = payments.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (p.receiptNumber.toLowerCase().includes(q)) return true;
    const inv = invoices.find(i => i.id === p.invoiceId);
    if (inv && inv.invoiceNumber.toLowerCase().includes(q)) return true;
    let customerName = '';
    if (p.customerType === 'Patient') {
      const pat = patients.find(pat => pat.id === p.customerId);
      if (pat) customerName = `${pat.firstName} ${pat.lastName}`;
    } else {
      const c = walkInClients.find(c => c.id === p.customerId);
      if (c) customerName = c.name;
    }
    return customerName.toLowerCase().includes(q);
  });
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn') => {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    } else {
      const c = walkInClients.find(c => c.id === customerId);
      return c ? c.name : 'Unknown Client';
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const invoice = invoices.find(i => i.id === formData.invoiceId);
    if (!invoice) {
      alert('Please select a valid invoice.');
      return;
    }
    const amountPaid = parseFloat(formData.amount);
    if (isNaN(amountPaid) || amountPaid <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    if (amountPaid > invoice.balanceDue) {
      alert(`Amount cannot exceed the balance due (${formatCurrency(invoice.balanceDue)}).`);
      return;
    }
    const newPayment = {
      receiptNumber: nextReceiptNumber,
      invoiceId: invoice.id,
      customerId: invoice.customerId,
      customerType: invoice.customerType,
      date: new Date().toISOString(),
      amount: amountPaid,
      method: formData.method,
      cashier: currentUser?.id || '',
      notes: formData.notes,
      createdAt: new Date().toISOString()
    };
    addPayment(newPayment);
    const newAmountPaid = invoice.amountPaid + amountPaid;
    const newBalanceDue = invoice.totalAmount - newAmountPaid;
    let newStatus = invoice.status;
    if (newBalanceDue <= 0) {
      newStatus = 'Paid';
    } else if (newAmountPaid > 0) {
      newStatus = 'Partially Paid';
    }
    updateInvoice(invoice.id, {
      amountPaid: newAmountPaid,
      balanceDue: newBalanceDue,
      status: newStatus
    });
    logBillingAudit('Record Payment', `Recorded payment of ${formatCurrency(amountPaid)} for invoice ${invoice.invoiceNumber}`);
    setShowModal(false);
    setFormData({
      invoiceId: '',
      amount: '',
      method: 'Cash',
      notes: ''
    });
  };
  return <div className="space-y-6" data-id="element-1335">
      <div className="flex justify-between items-center" data-id="element-1336">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1337">Payments Received</h1>
        <Button onClick={() => setShowModal(true)} data-id="element-1338">
          <Plus className="w-4 h-4 mr-2" data-id="element-1339" /> Record Payment
        </Button>
      </div>

      <Card noPadding data-id="element-1340">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1341">
          <div className="relative max-w-md" data-id="element-1342">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1343">
              <Search className="h-5 w-5 text-gray-400" data-id="element-1344" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by receipt#, invoice#, or customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1345" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1346">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1347">
            <thead className="bg-gray-50" data-id="element-1348">
              <tr data-id="element-1349">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1350">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1351">
                  Receipt#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1352">
                  Invoice#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1353">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1354">
                  Method
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1355">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1356">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1357">
              {filteredPayments.length > 0 ? filteredPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(payment => {
              const inv = invoices.find(i => i.id === payment.invoiceId);
              return <tr key={payment.id} className="hover:bg-gray-50" data-id="element-1358">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1359">
                          {new Date(payment.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600" data-id="element-1360">
                          {payment.receiptNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-1361">
                          {inv?.invoiceNumber || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1362">
                          {getCustomerName(payment.customerId, payment.customerType)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1363">
                          {payment.method}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1364">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1365">
                          <button className="text-gray-400 hover:text-blue-600 mx-1" title="View Receipt" onClick={() => setViewingPayment(payment)} data-id="element-1366">
                            <Eye className="w-4 h-4" data-id="element-1367" />
                          </button>
                        </td>
                      </tr>;
            }) : <tr data-id="element-1368">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1369">
                    No payments found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Record Payment Modal */}
      {showModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1370">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-1371">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1372">
              Record Payment
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1373">
              <Select label="Select Invoice" required value={formData.invoiceId} onChange={e => {
            const inv = invoices.find(i => i.id === e.target.value);
            setFormData({
              ...formData,
              invoiceId: e.target.value,
              amount: inv ? inv.balanceDue.toString() : ''
            });
          }} options={[{
            value: '',
            label: '-- Select Unpaid Invoice --'
          }, ...unpaidInvoices.map(i => ({
            value: i.id,
            label: `${i.invoiceNumber} - ${getCustomerName(i.customerId, i.customerType)} (${formatCurrency(i.balanceDue)} due)`
          }))]} data-id="element-1374" />
              <Input label="Amount Received (ZMW)" type="number" step="0.01" required value={formData.amount} onChange={e => setFormData({
            ...formData,
            amount: e.target.value
          })} data-id="element-1375" />
              <Select label="Payment Method" required value={formData.method} onChange={e => setFormData({
            ...formData,
            method: e.target.value as any
          })} options={[{
            value: 'Cash',
            label: 'Cash'
          }, {
            value: 'Mobile Money',
            label: 'Mobile Money'
          }, {
            value: 'Debit Card',
            label: 'Debit Card'
          }, {
            value: 'Credit Card',
            label: 'Credit Card'
          }, {
            value: 'Bank Transfer',
            label: 'Bank Transfer'
          }, {
            value: 'Insurance',
            label: 'Insurance'
          }, {
            value: 'Corporate Account',
            label: 'Corporate Account'
          }]} data-id="element-1376" />
              <div className="mt-4" data-id="element-1377">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1378">
                  Notes (Optional)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows={2} value={formData.notes} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} placeholder="Transaction ID, Check number, etc." data-id="element-1379" />
              </div>
              <div className="flex justify-end space-x-3 mt-6" data-id="element-1380">
                <Button variant="outline" type="button" onClick={() => setShowModal(false)} data-id="element-1381">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-1382">Save Payment</Button>
              </div>
            </form>
          </div>
        </div>}

      {viewingPayment && <ReceiptModal payment={viewingPayment} onClose={() => setViewingPayment(null)} data-id="element-1383" />}
    </div>;
};